import {
  ForwardedRef,
  useImperativeHandle,
  useState,
} from 'react';

import { toast } from 'react-toastify';

import InputTextFieldChips from 'shared/components/Input/InputTextFieldChips';
import { ExternalErrorOnItemsT } from 'shared/components/Input/InputTextFieldChips/InputTextFieldChips';
import { SETTINGS_TITLE } from 'shared/containers/ViewDetails/ViewDetails';
import { APIErrorT } from 'shared/models/api';
import { APIResponseDeviceConfigRegisterMultiT } from 'shared/models/device-configs';
import * as SourceM from 'shared/models/sources';
import api from 'utils/api';

export interface FormMultipleIDsEventEmitterT {
  onSubmit: () => void;
}
interface DeviceConfigItemFormMultipleIDsT {
  id: string;
}
interface DeviceConfigItemFormMultipleIDsProps {
  flowId: string;
  profileId: string;
  poolId: string;
  sourceTechnology: string; // From selected Flow
  // Allows to call functions from the parent component
  innerRef: ForwardedRef<FormMultipleIDsEventEmitterT>;
  // eslint-disable-next-line no-unused-vars
  onValidateFn: (areValid: 'all' | 'none' | 'some') => void;
}
// Add autoprovisioning in card and drop non matching in Device Profile
function DeviceConfigItemFormMultipleIDs({
  flowId,
  profileId,
  poolId,
  sourceTechnology,
  innerRef,
  onValidateFn,
}: DeviceConfigItemFormMultipleIDsProps) {
  const [deviceIds, setDevicesIds] = useState<Array<DeviceConfigItemFormMultipleIDsT>>([]);
  const [forceRerender, setForceRerender] = useState(0);
  const [
    externalErrorOnItems,
    setExternalErrorOnItems,
  ] = useState<Array<ExternalErrorOnItemsT>>([]);

  const formatItem = (input: string, setError: any): DeviceConfigItemFormMultipleIDsT | null => {
    const requirements = SourceM.getDeviceIDRequirement(sourceTechnology);
    if (requirements.regex) {
      const isValid = requirements.regex.test(input);
      if (isValid === false) {
        setError(`${requirements.regexHelper} (Current size: ${input.length})`);
        return null;
      }
    }
    return { id: input };
  };

  const formatLabel = (item: DeviceConfigItemFormMultipleIDsT) => `${item.id}`;

  const onChange = (newData: Array<DeviceConfigItemFormMultipleIDsT>) => {
    setDevicesIds(newData);
  };

  // Functions to trigger from parent component
  useImperativeHandle(innerRef, () => ({
    onSubmit() {
      if (deviceIds.length === 0) {
        toast.info('No Device IDs - Please press enter to add an Device ID');
        onValidateFn('none');
        return;
      }

      setForceRerender((count) => count + 1);
      setExternalErrorOnItems([]);

      api.deviceConfig.registerMulti(
        flowId,
        profileId,
        poolId,
        deviceIds.map((item) => item.id),
      )
        .then((resp: any) => {
          if (resp.status >= 400) {
            resp.json()
              .then((error: APIErrorT) => {
                const errorMessage = error?.message;
                if (errorMessage.includes('exceed the device limit')) {
                  toast.warn('Creation not possible: Organization maximum number of devices reached');
                  onValidateFn('none');
                  return;
                }
                toast.error(errorMessage);
                onValidateFn('none');
              });
          } else {
            resp.json()
              .then((data: APIResponseDeviceConfigRegisterMultiT) => {
                if (data?.deviceIdWithError.length) {
                  // Extract from deviceIdWithError: ["id: errorMessage"]
                  const errorsItem: ExternalErrorOnItemsT[] = data.deviceIdWithError.map((e) => {
                    const [id, errorMessage] = e.split(': ');
                    let tmpErrorMessage = errorMessage;
                    // Rename errors
                    if (errorMessage === 'device ID already exists') {
                      tmpErrorMessage = 'Id already used';
                    }
                    return ({
                      findItemFn: (item: any) => item.id === id,
                      message: tmpErrorMessage,
                    } as ExternalErrorOnItemsT);
                  });
                  setExternalErrorOnItems(errorsItem);
                  // Check if some deviceIds are valid
                  if (errorsItem.length < deviceIds.length) {
                    let currentDeviceIds = deviceIds;
                    // Keep only invalid deviceIds
                    currentDeviceIds = currentDeviceIds
                      .filter((deviceId: DeviceConfigItemFormMultipleIDsT) => {
                        const isInvalid = errorsItem.some((e) => e.findItemFn(deviceId));
                        return isInvalid;
                      });
                    setDevicesIds(currentDeviceIds);
                    toast.warn('Success to insert some of the Device IDs but other are invalid');
                    onValidateFn('some');
                  } else {
                    toast.error('None of the Device IDs is invalid');
                    onValidateFn('none');
                  }
                } else {
                  toast.success('Success to create new devices');
                  setDevicesIds([]);
                  onValidateFn('all');
                }
              });
          }
        });
    },
  }));

  return (
    <div className="form__section" style={{ marginTop: '1rem' }}>
      <h3>{ SETTINGS_TITLE }</h3>
      <InputTextFieldChips
        fullWidth
        name="Device IDs"
        formatItem={formatItem}
        formatLabel={formatLabel}
        onChange={onChange}
        defaultValues={deviceIds}
        externalErrorOnItems={externalErrorOnItems}
        forceRerender={forceRerender}
        defaultHelperText={`Press enter - ${SourceM.getDeviceIDRequirement(sourceTechnology).defaultHelper}`}
      />
    </div>
  );
}

export default DeviceConfigItemFormMultipleIDs;
